import { datadogRum } from '@datadog/browser-rum';

import { logger } from '@/utils/logger/logger';
import { createChunkFailureReporter, isChunkLoadError } from '@/utils/retry-lazy';

export type SplitScreenModule = typeof import('./split-screen');

/**
 * The single `import()` call site for the session tab tree, kept beside the
 * module it loads rather than inside either consumer.
 *
 * Both surfaces that mount the tree — the Evidence linked-session preview and
 * the agent-chat session artifact panel, which preloads it alongside its own
 * impl chunk — go through this thunk, so webpack sees one dynamic-import site
 * and emits one chunk with one in-flight request. Living here also keeps the
 * artifact panel's preload from taking a dependency on Chat.
 *
 * Holding it outside the component module additionally lets tests exercise a
 * rejected chunk request without replacing the SplitScreen implementation.
 */
export const loadSplitScreen = (): Promise<SplitScreenModule> => import('./split-screen');

/**
 * Shared by the preload thunk and the tab-strip error boundary. One instance
 * so the per-error-instance dedupe spans both: whichever observes a rejection
 * first reports it, and the other is a no-op.
 */
export const reportSplitScreenChunkFailure = createChunkFailureReporter({
  feature: 'session_tabs_split_screen',
  logMessage: '[session-tabs] SplitScreen chunk failed to load',
});

/**
 * Terminal `catch` handler for the artifact panel's preload of this chunk.
 * Chunk-shaped rejections go through the shared reporter above; anything else
 * — e.g. a module-evaluation throw — is reported under its own `preload`
 * phase, so the failure is attributed even if the tab strip never mounts.
 * Never throws: a throw from a terminal `catch` would turn the handled
 * rejection into an unhandled one.
 */
export const reportSplitScreenPreloadFailure = (error: unknown): void => {
  try {
    if (isChunkLoadError(error)) {
      reportSplitScreenChunkFailure(error);
      return;
    }
    logger.error('[session-tabs] SplitScreen preload failed', error);
    datadogRum.addError(error, {
      feature: 'session_tabs_split_screen',
      phase: 'preload',
    });
  } catch {
    // Reporting must never break the preload path itself.
  }
};
